/*
 *   File : index.js
 *   Author URI : https://evoqueinnovativelab.com
 *   Description : Index file for analytics container;
 *   Integrations : null
 *   Version : v1.0
 *   Created : 28-07-2026
 */

/*import packages*/
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/*import components*/
import { DataTable } from '../../components/dataFormating/index';
import { DownloadBtn } from '../../components/cta/index';
import { SearchInput, DateRangePicker } from '../../components/input/index';
import { Loader } from '../../components/loader';
import { EmptyNote, Toast } from '../../components/note';

/*import styles*/
import styles from '../../styles/components/searchInput.module.scss';

/* import services */
import devconfig from '../../config/config';
import APIPost from '../../services/postApi';

const Index = (props) => {

	const navigate = useNavigate();
	const [search, setSearch] = useState('');
	const [dates, setDates] = useState({
		start: null,
		end: null
	});
	const [investorData, setInvestorData] = useState([]);
	const [totalRecords, setTotalRecords] = useState(null);
	const [pageNum, setPageNum] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [downloadLoader, setDownloadLoader] = useState(false);
	const [loader, setLoader] = useState(true);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [sortBy, setSortBy] = useState(null);
	const [sortOrder, setSortOrder] = useState(null);

	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth'
		});
	}, []);

	useEffect(() => {
		_getInvestorsView();
		//eslint-disable-next-line
	}, [dates, pageNum, perPage, sortBy, sortOrder]);

	useEffect(() => {
		if (error !== null) {
			setTimeout(() => {
				setError(null);
			}, 3000);
		}
	}, [error]);

	useEffect(() => {
		if (success !== null) {
			setTimeout(() => {
				setSuccess(null);
			}, 3000);
		}
	}, [success]);

	/*---- zero-pad each "yy:mm:dd" segment so tenure values line up, e.g. "2:7:23" -> "02:07:23" ---*/
	const _formatTenure = (tenure) => {
		if (!tenure) return '--';
		return String(tenure).split(':').map((part) => part.padStart(2, '0')).join(':');
	};

	/*---- green for gains, red for losses ---*/
	const _gainLossClass = (value) => {
		if (value === undefined || value === null) return '';
		if (value > 0) return 'e-value-positive';
		if (value < 0) return 'e-value-negative';
		return '';
	};

	/*---- columns sortable by the backend, mapped to the sort_by value it expects ---*/
	const SERVER_SORT_FIELDS = {
		'Joined On': 'joined',
		'Current': 'current',
		'Invested': 'invested',
		'XIRR': 'xirr',
		'Absolute': 'absolute',
	};

	/*---- header click: server-sortable columns refetch, the rest sort the current page locally ---*/
	const _handleSort = (column, sortDirection) => {
		const backend_field = SERVER_SORT_FIELDS[column.name];
		if (backend_field) {
			setPageNum(1);
			setSortBy(backend_field);
			setSortOrder(sortDirection);
			return;
		}

		const compare = (a, b) => {
			const valueA = column.selector(a);
			const valueB = column.selector(b);
			if (valueA === valueB) return 0;
			return valueA > valueB ? 1 : -1;
		};
		setInvestorData(prev => {
			const sorted = [...prev].sort(compare);
			return sortDirection === 'desc' ? sorted.reverse() : sorted;
		});
	};

	/*---- columns for investors view table ---*/
	const columns = [
		{
			name: 'Client Name',
			selector: row => row.client_name ? row.client_name : '--',
			sortable: true,
			left: true,
			minWidth: "200px",
		},
		{
			name: 'Joined On',
			selector: row => row.joined_on ? row.joined_on : '--',
			sortable: true,
			left: true,
			minWidth: "130px",
		},
		{
			name: 'Tenure (yy:mm:dd)',
			selector: row => _formatTenure(row.tenure),
			sortable: false,
			left: true,
			minWidth: "200px",
		},
		{
			name: 'Current',
			selector: row => row.current ? row.current : '--',
			sortable: true,
			right: true,
			minWidth: "150px",
		},
		{
			name: 'Invested',
			selector: row => row.invested ? row.invested : '--',
			sortable: true,
			right: true,
			minWidth: "150px",
		},
		{
			name: 'XIRR',
			selector: row => row.xirr_value,
			sortable: true,
			right: true,
			minWidth: "110px",
			cell: row => row.xirr ? <span className={_gainLossClass(row.xirr_value)}>{row.xirr}</span> : '--'
		},
		{
			name: 'Absolute',
			selector: row => row.absolute,
			sortable: true,
			right: true,
			minWidth: "110px",
			cell: row => row.absolute !== undefined && row.absolute !== null ? <span className={_gainLossClass(row.absolute)}>{row.absolute}%</span> : '--'
		},
		{
			name: 'Unrealized',
			selector: row => row.unrealized ? row.unrealized : '--',
			sortable: false,
			right: true,
			minWidth: "150px",
		},
		{
			name: 'Realized',
			selector: row => row.realized ? row.realized : '--',
			sortable: false,
			right: true,
			minWidth: "150px",
		},
		{
			name: 'SIP',
			selector: row => row.sip,
			sortable: true,
			right: true,
			minWidth: "150px",
			cell: row => row.sip ? row.sip.toLocaleString('EN-in', { maximumFractionDigits: 0 }) : '--'
		},
		{
			name: 'Annual Income',
			selector: row => row.annual_income,
			sortable: true,
			right: true,
			minWidth: "160px",
			cell: row => row.annual_income ? row.annual_income.toLocaleString('EN-in', { maximumFractionDigits: 0 }) : '--'
		},
	];

	/*--------- investors view list -----------*/
	const _getInvestorsView = (query) => {
		setLoader(true);

		let url = devconfig.admin + '/user/investors-view';

		let data = JSON.stringify({
			from_date: dates.start,
			to_date: dates.end,
			query: query ? query.value : search,
			page_num: pageNum,
			page_size: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
		});

		APIPost(url, data).then((response) => {
			if (response.status_code === 200) {
				setInvestorData(response.data.data ?? response.data);
				setTotalRecords(response.data.total_records);
				setLoader(false);
			}
			else {
				setInvestorData([]);
				setError(response.message);
				setLoader(false);
			}
		});
	}

	/*---- download investors view api--*/
	const _downloadData = () => {
		setDownloadLoader(true);

		let url = devconfig.admin + '/user/investors-view-download';

		let data = JSON.stringify({
			from_date: dates.start,
			to_date: dates.end,
			query: search,
		});

		APIPost(url, data).then((response) => {
			if (response.status_code === 200) {
				setDownloadLoader(false);
				setSuccess("The report has been sent to your email.");
			}
			else {
				setDownloadLoader(false);
				setError(response.message);
			}
		});
	}

	const _handlePageChange = (page_num) => {
		setPageNum(page_num);
	};

	const _handlePerPage = (per_page) => {
		setPerPage(per_page);
	};

	return (
		<div className="row">
			{/*------------------ Investors View table------------------ */}
			<div className="col-lg-12">
				<div className="card e-dashboard-card border-0 py-1">
					<div className="row pt-2">
						<div className="col-lg-11">
							<h6 className="e-section-title mb-0">Investors View</h6>
						</div>

						<div className="e-filter-wraper e-sort mt-1">
							<div className="row align-items-end">
								<div className="col-lg-3 pe-1 col-6">
									<label className="mb-1">From - Till (Joined Date)</label>
									<DateRangePicker
										start={dates.start}
										end={dates.end}
										selectPrevious={true}
										onDatesChange={(start, end) => {
											setDates({ start: start, end: end });
											setPageNum(1);
											setPerPage(10);
										}}
									/>
								</div>
								<div className="col-lg-6" />
								<div className="col-lg-3 ps-1 pe-0">
									<div className="d-flex align-items-center bg-white p-1">
										<div className="position-relative">
											<SearchInput
												placeholder="Search with client name"
												value={search}
												type="text"
												valueChange={(value) => {
													setSearch(value);
													if (value === "") {
														_getInvestorsView({ value: "" });
														setPageNum(1);
														setPerPage(10);
													}
												}}
												onSearch={(value) => {
													_getInvestorsView({ value: value });
													setPageNum(1);
													setPerPage(10);
												}}
											/>
											{
												search?.length > 0 ?
													<>
														<span className={`${styles.e_search_submit} `} onClick={() => {
															_getInvestorsView();
															setPageNum(1);
															setPerPage(10);
														}} ></span>
														<button className={`${styles.e_search_close} pe-3`}
															onClick={() => {
																setSearch("");
																_getInvestorsView({ value: "" });
																setPageNum(1);
																setPerPage(10);
															}} ></button>
													</>
													:
													null
											}
										</div>
										<span className="ms-2">
											<DownloadBtn loader={downloadLoader}
												check={['check']}
												onClick={() => {
													_downloadData();
												}}
											/>
										</span>
									</div>
								</div>
							</div>
						</div>

						{
							loader ?
								<Loader />
								: investorData.length > 0 ?
									<div className="col-lg-12 px-0">
										<DataTable
											pagination={true}
											columns={columns}
											data={investorData}
											perPage={perPage}
											paginationServer={true}
											paginationDefaultPage={pageNum}
											paginationTotalRows={totalRecords}
											onChangeRowsPerPage={_handlePerPage}
											onChangePage={_handlePageChange}
											rowClick={(row) => navigate(`/user-detail?id=${row.user_id}`)}
											sortServer={true}
											onSort={_handleSort}
										/>
									</div>
									:
									<EmptyNote note="No investor data to show" />
						}
					</div>
				</div>
			</div>

			{/* error toast */}
			{
				error !== null ?
					<Toast
						data={error}
						type="fail"
						id="1"
					/>
					:
					null
			}
			{/* Success toast */}
			{
				success !== null ?
					<Toast data={success}
						type="success"
						id="2" />
					:
					null
			}
		</div>
	);
}

export default Index;
